// Payment System v3.0 PR 9 — Phoenix-Backed Resale workflow React Query
// hooks (design spec §8.5). Preview is a pure mutation; mutating endpoints
// invalidate the loan detail surface + the resale-link query.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { resaleApi } from "@/api/portfolioAdmin/resale";
import type {
  FinancialClosingRequest,
  ResaleClosingExecuteRequest,
  ResaleClosingPreviewRequest,
} from "@/types/resale";

export function usePreviewResale(loanId: string) {
  return useMutation({
    mutationFn: (body: ResaleClosingPreviewRequest) =>
      resaleApi.previewResale(loanId, body),
  });
}

export function useExecuteResale(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ResaleClosingExecuteRequest) =>
      resaleApi.executeResale(loanId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["loans", loanId] });
      qc.invalidateQueries({ queryKey: ["loans"] });
      qc.invalidateQueries({ queryKey: ["resale-link", loanId] });
    },
  });
}

export function useMatureBridge(spreadNoteLoanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => resaleApi.matureBridge(spreadNoteLoanId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["loans", spreadNoteLoanId] });
      qc.invalidateQueries({ queryKey: ["resale-link", spreadNoteLoanId] });
    },
  });
}

export function usePreviewFinancialClosing(spreadNoteLoanId: string) {
  return useMutation({
    mutationFn: (body: FinancialClosingRequest) =>
      resaleApi.previewFinancialClosing(spreadNoteLoanId, body),
  });
}

export function useExecuteFinancialClosing(spreadNoteLoanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FinancialClosingRequest) =>
      resaleApi.executeFinancialClosing(spreadNoteLoanId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["loans", spreadNoteLoanId] });
      qc.invalidateQueries({ queryKey: ["loans"] });
      qc.invalidateQueries({ queryKey: ["spv-pools"] });
      qc.invalidateQueries({ queryKey: ["resale-link", spreadNoteLoanId] });
    },
  });
}

export function useResaleLink(loanId: string | undefined) {
  return useQuery({
    queryKey: ["resale-link", loanId],
    queryFn: () => resaleApi.getResaleLink(loanId!),
    enabled: !!loanId,
    // 404 means "no resale link for this loan" — that's a valid answer,
    // don't retry.
    retry: false,
  });
}
